import React from "react";
import {
  DonateHero,
  HeroLeft,
  HeroHeadline,
  HeroBody,
  HeroImage,
  HeroLogoMark,
  HeroLogoStem,
  ProtectButton,
  MonthlyLink,
  TrustBar,
  TrustItem,
  WhyMonthlySection,
  WhyMonthlyInner,
  WhyMonthlyLeft,
  WhyMonthlyHeading,
  BulletList,
  BulletItem,
  MonthlyCard,
  MonthlyCardLabel,
  MonthlyCardButton,
  SectionDivider,
  DonateNowSection,
  DonateNowButton,
  WhatYourGiftSection,
  GiftSectionHeading,
  GiftGrid,
  GiftCard,
  GiftCardHeader,
  GiftCardBody,
  GiftCardHighlight,
  GivingLevelsSection,
  GivingLevelsCard,
  GivingLevelsHeader,
  GivingLevelsBody,
  GivingLevelRow,
  GivingLevelBadge,
  GivingLevelName,
  MakeImpactSection,
  ImpactDividerLine,
  MakeImpactButton,
  OtherWaysSection,
  OtherWaysHeading,
  OtherWaysInner,
  MailColumn,
  MailLabel,
  MailOrgName,
  MailAddress,
  DonateCalloutColumn,
  DonateCalloutText
} from "./styled.components";
import GreenCheck from "./GreenCheck";
import {
  siteDarkGreen,
  siteLightBlue,
  sitePurple,
  siteYellow
} from "../../providers/Theme";
import DonateForm from "../../components/DonateForm";
import CollaboratingWithLeaders from "../../components/CollaboratingWithLeaders";

const Donate: React.FC = () => {
  return (
    <>
      {/* Section 1: Hero */}
      <DonateHero>
        <HeroLogoMark />
        <HeroLogoStem />
        <HeroLeft>
          <HeroHeadline>Help Protect Children With Food Allergies</HeroHeadline>
          <HeroBody>
            Your donation supports free food allergy education, training, and
            access to life-saving resources in schools and communities.
          </HeroBody>
          <DonateForm
            triggerElement={
              <ProtectButton>Protect a Child Today</ProtectButton>
            }
          />
          <MonthlyLink>or become a monthly protector</MonthlyLink>
        </HeroLeft>
        <HeroImage />
      </DonateHero>

      {/* Section 2: Trust Bar */}
      <TrustBar>
        <TrustItem>
          <GreenCheck /> 501(c)(3) non-profit
        </TrustItem>
        <TrustItem>
          <GreenCheck /> Secure donation processing
        </TrustItem>
      </TrustBar>

      {/* Section 3: Why Monthly Giving Matters */}
      <WhyMonthlySection>
        <WhyMonthlyInner>
          <WhyMonthlyLeft>
            <WhyMonthlyHeading>Why Monthly Giving Matters</WhyMonthlyHeading>
            <BulletList>
              <BulletItem>
                <GreenCheck /> Provides predictable funding for year-round
                outreach
              </BulletItem>
              <BulletItem>
                <GreenCheck /> Keeps training FREE for schools and food service
                teams
              </BulletItem>
              <BulletItem>
                <GreenCheck /> Sustains policy advocacy and national expansion
              </BulletItem>
            </BulletList>
          </WhyMonthlyLeft>
          <MonthlyCard>
            <MonthlyCardLabel>
              Become <span>a monthly</span> protector
            </MonthlyCardLabel>
            <DonateForm
              triggerElement={
                <MonthlyCardButton>
                  <GreenCheck /> Most Impactful
                </MonthlyCardButton>
              }
            />
          </MonthlyCard>
        </WhyMonthlyInner>
      </WhyMonthlySection>

      <SectionDivider />

      {/* Section 4: Donate Now CTA */}
      <DonateNowSection>
        <DonateForm
          triggerElement={<DonateNowButton>Donate Now</DonateNowButton>}
        />
      </DonateNowSection>

      <SectionDivider />

      {/* Section 5: What Your Gift Makes Possible */}
      <WhatYourGiftSection>
        <GiftSectionHeading>What Your Gift Makes Possible</GiftSectionHeading>
        <GiftGrid>
          <div>
            <DonateForm
              triggerElement={
                <GiftCard>
                  <GiftCardHeader $color={siteLightBlue}>$25</GiftCardHeader>
                  <GiftCardBody $bg="#e5f2ff">
                    <GiftCardHighlight $color={siteLightBlue}>
                      Trains school staff
                    </GiftCardHighlight>{" "}
                    to recognize an allergic emergency
                  </GiftCardBody>
                </GiftCard>
              }
            />
          </div>
          <div>
            <DonateForm
              triggerElement={
                <GiftCard>
                  <GiftCardHeader $color={sitePurple}>$100</GiftCardHeader>
                  <GiftCardBody $bg="#fae8fa">
                    <GiftCardHighlight $color={sitePurple}>
                      Supports outreach
                    </GiftCardHighlight>{" "}
                    to protect Arizona schools
                  </GiftCardBody>
                </GiftCard>
              }
            />
          </div>
          <div>
            <DonateForm
              triggerElement={
                <GiftCard>
                  <GiftCardHeader $color={siteDarkGreen}>$250</GiftCardHeader>
                  <GiftCardBody $bg="#e8f7ec">
                    <GiftCardHighlight $color={siteDarkGreen}>
                      Advances national
                    </GiftCardHighlight>{" "}
                    policy and grant expansion
                  </GiftCardBody>
                </GiftCard>
              }
            />
          </div>
          <div>
            <DonateForm
              triggerElement={
                <GiftCard>
                  <GiftCardHeader $color={siteYellow}>$500</GiftCardHeader>
                  <GiftCardBody $bg="#fff8dc">
                    <GiftCardHighlight $color="#c48a00">
                      Expands Kyah's EPICourse
                    </GiftCardHighlight>{" "}
                    access at no cost to users
                  </GiftCardBody>
                </GiftCard>
              }
            />
          </div>
        </GiftGrid>
      </WhatYourGiftSection>

      <SectionDivider />

      {/* Section 6: Giving Levels */}
      <GivingLevelsSection>
        <GivingLevelsCard>
          <GivingLevelsHeader>Giving Levels</GivingLevelsHeader>
          <GivingLevelsBody>
            <DonateForm
              triggerElement={
                <GivingLevelRow>
                  <GivingLevelBadge $color={siteLightBlue}>
                    $100
                  </GivingLevelBadge>
                  <GivingLevelName $color={siteLightBlue}>
                    Allergy Ally
                  </GivingLevelName>
                </GivingLevelRow>
              }
            />
            <DonateForm
              triggerElement={
                <GivingLevelRow>
                  <GivingLevelBadge $color={sitePurple}>$250</GivingLevelBadge>
                  <GivingLevelName $color={sitePurple}>
                    Allergy Advocate
                  </GivingLevelName>
                </GivingLevelRow>
              }
            />
            <DonateForm
              triggerElement={
                <GivingLevelRow>
                  <GivingLevelBadge $color={siteDarkGreen}>
                    $500
                  </GivingLevelBadge>
                  <GivingLevelName $color={siteDarkGreen}>
                    Allergy Angel
                  </GivingLevelName>
                </GivingLevelRow>
              }
            />
          </GivingLevelsBody>
        </GivingLevelsCard>
      </GivingLevelsSection>

      {/* Section 7: Make Your Impact CTA */}
      <MakeImpactSection>
        <ImpactDividerLine />
        <div>
          <DonateForm
            triggerElement={
              <MakeImpactButton>Make Your Impact</MakeImpactButton>
            }
          />
        </div>
        <ImpactDividerLine />
      </MakeImpactSection>

      {/* Section 8: Trusted Partners carousel */}
      <CollaboratingWithLeaders
        heading="Trusted by Schools, Foundations, & Community Partners"
        accent=""
        uppercaseHeading={false}
        caption="Thank you to the partners and sponsors who make this work possible."
      />

      <SectionDivider />

      {/* Section 9: Other Ways to Give */}
      <OtherWaysSection>
        <OtherWaysHeading>Other Ways to Give</OtherWaysHeading>
        <OtherWaysInner>
          <MailColumn>
            <MailLabel>Mail a check to:</MailLabel>
            <MailOrgName>Kyah Rayne Foundation</MailOrgName>
            <MailAddress>
              3104 E. Camelback Road, Ste. 1172
              <br />
              Phoenix, Arizona 85016
            </MailAddress>
          </MailColumn>
          <DonateCalloutColumn>
            <DonateCalloutText>
              <strong>In honor of Kyah,</strong> we are building safer
              environments so no child faces an allergy emergency without help.
            </DonateCalloutText>
            <DonateForm
              triggerElement={<DonateNowButton>Donate Now</DonateNowButton>}
            />
          </DonateCalloutColumn>
        </OtherWaysInner>
      </OtherWaysSection>
    </>
  );
};

export default Donate;
